import { MutableRefObject, useCallback, useRef, useState } from 'react';

import { BIZY_VIA, BonusVia } from '../events';

/**
 * Resolves `application_name` / Bonus#via for a give-box submit.
 *
 * Attribution is body-centric (Option A): a Bizy "Edit in givebox" hand-off is
 * credited to Bizy only when Bizy drafted the message body and the user hasn't
 * cleared/replaced it. A bodyless hand-off (recipients/amount/hashtags only) or
 * a from-scratch rewrite is not credited. Accepting a gratitude-gauge suggestion
 * attributes independently. When the hand-off is not credited, the submit records
 * the surface's own channel (`baseVia`, e.g. `ms_teams`) rather than dropping to
 * web.
 */
export function resolveGiveBoxVia({
	handOffVia,
	baseVia,
	bizyHandOffHadContent,
	bizyBodyCleared,
	gaugeAssisted,
}: {
	handOffVia?: BonusVia;
	baseVia?: BonusVia;
	bizyHandOffHadContent: boolean;
	bizyBodyCleared: boolean;
	gaugeAssisted: boolean;
}): BonusVia | undefined {
	const bizyFromHandOff = handOffVia === BIZY_VIA && bizyHandOffHadContent && !bizyBodyCleared;
	if (bizyFromHandOff || gaugeAssisted) return BIZY_VIA;
	return baseVia;
}

interface UseBizyViaAttributionConfig {
	/** Channel carried on the give-box open event ("bizy" for a Bizy hand-off). */
	handOffVia?: BonusVia;
	/** The surface's own channel (e.g. "ms_teams"); recorded when Bizy isn't credited. */
	baseVia?: BonusVia;
	/**
	 * Whether the open event handed off message body content. Computed once from
	 * the open payload (see `openEventHasMessageContent`); `sessionKey` remounts
	 * per open, so this is a mount-time constant and needs no timing-sensitive
	 * editor sniff.
	 */
	bizyHandOffHadContent: boolean;
	/** Draft-assist accepted-suggestion log; a non-empty log means gauge-assisted. */
	acceptedSuggestionsRef: MutableRefObject<unknown[]>;
}

/**
 * Owns "sent with Bizy's help" attribution for a give-box session.
 *
 * `effectiveVia` is what the submit should record. `onEditorBodyChange` takes
 * whether the editor still has a non-token message body (recipient/amount/hashtag
 * pills don't count) and, once a Bizy-drafted body is cleared, drops attribution
 * (Judith's "small tweak" keeps it; a from-scratch rewrite does not).
 *
 * Body presence is read from the TipTap doc, not `getText()` + token stripping:
 * resolved recipient pills render as display names (no "@") and hashtags may drop
 * the "#", so string stripping under-detects an empty body on the Desktop path.
 */
export function useBizyViaAttribution({ handOffVia, baseVia, bizyHandOffHadContent, acceptedSuggestionsRef }: UseBizyViaAttributionConfig) {
	const [bizyBodyCleared, setBizyBodyCleared] = useState(false);
	// Seeded from the open event: a hand-off that prefills a body (Desktop `text`
	// / legacy `editorContent`) has it in the editor at mount and fires no initial
	// onUpdate, so a delete-as-first-edit still counts as a clear.
	const hadBizyBodyRef = useRef(bizyHandOffHadContent);

	const gaugeAssisted = acceptedSuggestionsRef.current.length > 0;
	const effectiveVia = resolveGiveBoxVia({ handOffVia, baseVia, bizyHandOffHadContent, bizyBodyCleared, gaugeAssisted });

	const onEditorBodyChange = useCallback(
		(bodyHasText: boolean) => {
			// Only a Bizy hand-off that carried a body can be "cleared". A bodyless
			// hand-off is never credited under the body-centric rule, so typing a
			// message must not arm it.
			if (handOffVia !== BIZY_VIA || !bizyHandOffHadContent) return;
			if (bodyHasText) {
				hadBizyBodyRef.current = true;
			} else if (hadBizyBodyRef.current) {
				setBizyBodyCleared(true);
			}
		},
		[handOffVia, bizyHandOffHadContent]
	);

	return { effectiveVia, onEditorBodyChange };
}
